import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';

import { Button } from '../ui/button';

/**
 * AudioRecorder component with voice activity detection and auto-trimming.
 *
 * Features:
 * - Request microphone permission
 * - Visual audio level meter
 * - Manual start/stop recording
 * - Voice activity detection for auto-trimming
 * - Trims silence to ~200ms before/after speech
 *
 * @param {function} onRecordingComplete - Called with the recorded audio Blob
 * @param {boolean} disabled - Disable the recorder
 */
const AudioRecorder = ({ onRecordingComplete, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const animationFrameRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // Voice activity detection state
  const vadStateRef = useRef({
    speechStartTime: null,
    speechEndTime: null,
    isSpeaking: false,
    silenceStartTime: null,
    peakLevel: 0,
  });

  // Configuration
  const SILENCE_THRESHOLD = 0.02; // Audio level below this is considered silence
  const SPEECH_THRESHOLD = 0.04; // Audio level above this triggers speech detection
  const SILENCE_DURATION_MS = 500; // How long silence before considered "stopped speaking"
  const TRIM_BUFFER_MS = 200; // Buffer to keep before/after speech

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    mediaRecorderRef.current = null;
    setAudioLevel(0);
    setRecordingDuration(0);
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const analyzeAudioLevel = useCallback(() => {
    if (!analyserRef.current || !isRecording) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate RMS (root mean square) for better level detection
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = dataArray[i] / 255;
      sum += normalized * normalized;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    setAudioLevel(rms);

    // Update VAD state
    const vad = vadStateRef.current;
    const now = Date.now();

    if (rms > vad.peakLevel) {
      vad.peakLevel = rms;
    }

    if (rms > SPEECH_THRESHOLD) {
      if (!vad.isSpeaking) {
        vad.isSpeaking = true;
        vad.speechStartTime = vad.speechStartTime || now;
      }
      vad.silenceStartTime = null;
      vad.speechEndTime = now;
    } else if (rms < SILENCE_THRESHOLD && vad.isSpeaking) {
      if (!vad.silenceStartTime) {
        vad.silenceStartTime = now;
      } else if (now - vad.silenceStartTime > SILENCE_DURATION_MS) {
        // Extended silence detected after speech - could auto-stop here
        vad.speechEndTime = vad.silenceStartTime;
      }
    }

    animationFrameRef.current = requestAnimationFrame(analyzeAudioLevel);
  }, [isRecording]);

  const startRecording = async () => {
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      streamRef.current = stream;
      setPermissionDenied(false);

      // Set up audio analysis
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Reset VAD state
      vadStateRef.current = {
        speechStartTime: null,
        speechEndTime: null,
        isSpeaking: false,
        silenceStartTime: null,
        peakLevel: 0,
      };

      // Set up MediaRecorder
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });

          // Process and trim the audio
          const trimmedBlob = await trimAudioBlob(blob);

          if (onRecordingComplete) {
            onRecordingComplete(trimmedBlob);
          }
        } catch (error) {
          console.error('Error processing recording:', error);
          // Fall back to untrimmed audio
          const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
          if (onRecordingComplete) {
            onRecordingComplete(blob);
          }
        } finally {
          setIsProcessing(false);
          cleanup();
        }
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);

      // Start duration timer
      const startTime = Date.now();
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      // Start audio level analysis
      analyzeAudioLevel();

    } catch (error) {
      console.error('Error accessing microphone:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      }
      cleanup();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const trimAudioBlob = async (blob) => {
    // Create an audio context for processing
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Find speech boundaries using amplitude analysis
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;

      // Find first sample above threshold (speech start)
      let speechStartSample = 0;
      const windowSize = Math.floor(sampleRate * 0.01); // 10ms windows

      for (let i = 0; i < channelData.length - windowSize; i += windowSize) {
        let windowMax = 0;
        for (let j = 0; j < windowSize; j++) {
          windowMax = Math.max(windowMax, Math.abs(channelData[i + j]));
        }
        if (windowMax > SPEECH_THRESHOLD) {
          speechStartSample = Math.max(0, i - Math.floor(sampleRate * (TRIM_BUFFER_MS / 1000)));
          break;
        }
      }

      // Find last sample above threshold (speech end)
      let speechEndSample = channelData.length;
      for (let i = channelData.length - windowSize; i >= 0; i -= windowSize) {
        let windowMax = 0;
        for (let j = 0; j < windowSize; j++) {
          windowMax = Math.max(windowMax, Math.abs(channelData[i + j]));
        }
        if (windowMax > SPEECH_THRESHOLD) {
          speechEndSample = Math.min(channelData.length, i + windowSize + Math.floor(sampleRate * (TRIM_BUFFER_MS / 1000)));
          break;
        }
      }

      // If no speech detected, return original
      if (speechStartSample >= speechEndSample) {
        audioContext.close();
        return blob;
      }

      // Create trimmed buffer
      const trimmedLength = speechEndSample - speechStartSample;
      const trimmedBuffer = audioContext.createBuffer(
        audioBuffer.numberOfChannels,
        trimmedLength,
        sampleRate
      );

      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        const sourceData = audioBuffer.getChannelData(channel);
        const targetData = trimmedBuffer.getChannelData(channel);
        for (let i = 0; i < trimmedLength; i++) {
          targetData[i] = sourceData[speechStartSample + i];
        }
      }

      // Convert back to blob (as WAV for better compatibility)
      const wavBlob = await audioBufferToWav(trimmedBuffer);
      audioContext.close();
      return wavBlob;

    } catch (error) {
      console.error('Error trimming audio:', error);
      audioContext.close();
      return blob; // Return original if trimming fails
    }
  };

  // Convert AudioBuffer to WAV blob
  const audioBufferToWav = async (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const dataLength = buffer.length * blockAlign;
    const bufferLength = 44 + dataLength;

    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);

    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, bufferLength - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);

    // Write audio data
    const channels = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (permissionDenied) {
    return (
      <div className="text-sm text-red-600">
        Microphone access denied. Please allow microphone access in your browser settings.
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {isRecording ? (
        <>
          {/* Recording indicator with level meter */}
          <div className="flex items-center gap-2">
            <div className="relative w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-red-500 transition-all duration-75"
                style={{ width: `${Math.min(100, audioLevel * 500)}%` }}
              />
            </div>
            <span className="text-sm text-red-600 font-medium animate-pulse">
              {formatDuration(recordingDuration)}
            </span>
          </div>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={stopRecording}
          >
            <Square className="h-4 w-4 mr-2" />
            Stop
          </Button>
        </>
      ) : isProcessing ? (
        <Button type="button" variant="outline" size="sm" disabled>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Processing...
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={startRecording}
          disabled={disabled}
          className="text-red-600 border-red-300 hover:bg-red-50"
        >
          <Mic className="h-4 w-4 mr-2" />
          Record Audio
        </Button>
      )}
    </div>
  );
};

export default AudioRecorder;
